import React, { useState } from 'react';

import { dateTime, GrafanaTheme2, rangeUtil, RawTimeRange, TimeRange } from '@grafana/data';
import { Button, DateTimePicker, TimeZonePicker, useStyles2 } from '@grafana/ui';

import { css } from '@emotion/css';

interface SiftTimePickerProps {
  value: TimeRange;
  onChange: (timeRange: TimeRange) => void;
}

export const SiftTimePicker = ({ value, onChange }: SiftTimePickerProps) => {
  const styles = useStyles2(getStyles);

  const [timeZone, setTimeZone] = useState<string>(Intl.DateTimeFormat().resolvedOptions().timeZone);

  const setToNow = () => {
    const now = dateTime();
    onChange({
      to: now,
      from: value.from,
      raw: {
        to: now.toISOString(),
        from: value.raw.from,
      },
    });
  };

  const applyPresetRange = (hoursBack: number) => {
    const toTmp = value.to;
    const fromTmp = dateTime(value.to).subtract(hoursBack, 'hour');
    onChange({ from: fromTmp, to: toTmp, raw: { from: fromTmp.toISOString(), to: toTmp.toISOString() } });
  };

  const onCopy = () => {
    const raw: RawTimeRange = { from: value.raw.from, to: value.raw.to };
    navigator.clipboard.writeText(JSON.stringify(raw));
  };

  const onPaste = async () => {
    const raw = await navigator.clipboard.readText();
    let range;

    try {
      range = JSON.parse(raw);
    } catch (error) {
      // if they arent pasting a valid json date, then ignore it
      console.error(error);
      return;
    }

    const newRange = rangeUtil.convertRawToRange(range);
    onChange(newRange);
  };

  const onTimeZoneChange = (tz: string | undefined) => {
    const selectedTimeZone =
      tz === undefined || tz === 'browser' || tz === '' ? Intl.DateTimeFormat().resolvedOptions().timeZone : tz;
    setTimeZone(selectedTimeZone);
  };

  return (
    <div className={styles.container}>
      <div className={styles.from}>
        <div className={styles.subHeader}>
          <label>From</label>
          <div>
            <a className={styles.shortcutBox} onClick={() => applyPresetRange(1)}>
              -1 hour
            </a>
            <a className={styles.shortcutBox} onClick={() => applyPresetRange(3)}>
              -3 hours
            </a>
            <a className={styles.shortcutBox} onClick={() => applyPresetRange(6)}>
              -6 hours
            </a>
          </div>
        </div>
        <DateTimePicker
          date={value.from}
          timeZone={timeZone}
          onChange={(newValue) => {
            if (newValue !== undefined) {
              onChange({
                ...value,
                raw: {
                  to: value.raw.to,
                  from: newValue.toISOString(),
                },
                from: newValue,
              });
            }
          }}
        />
      </div>
      <div className={styles.to}>
        <div className={styles.subHeader}>
          <label>To</label>
          <div>
            <a className={styles.shortcutBox} onClick={setToNow}>
              Now
            </a>
          </div>
        </div>
        <DateTimePicker
          date={value.to}
          timeZone={timeZone}
          onChange={(newValue) => {
            if (newValue !== undefined) {
              onChange({
                ...value,
                raw: {
                  from: value.raw.from,
                  to: newValue.toISOString(),
                },
                to: newValue,
              });
            }
          }}
        />
      </div>
      <div className={styles.timezone}>
        <label>Time Zone</label>
        <TimeZonePicker includeInternal={true} value={timeZone} onChange={onTimeZoneChange} />
      </div>
      <div className={styles.icons}>
        <div className={styles.copyPaste}>
          <Button tooltip="Copy time range" icon="copy" onClick={onCopy} variant="secondary" />
          <Button tooltip="Paste time range" icon="clipboard-alt" onClick={onPaste} variant="secondary" />
        </div>
      </div>
    </div>
  );
};

const getStyles = (theme: GrafanaTheme2) => ({
  container: css`
    margin-top: 12px;
    display: grid;
    grid-template-columns: 1.5fr 1.5fr 0.5fr;
    grid-template-rows: 1fr 1fr 1fr;
    gap: 10px 0px;
    grid-auto-flow: row;
    grid-template-areas:
      'from to icons'
      'timezone timezone icons';
  `,
  from: css`
    grid-area: from;
  `,

  to: css`
    grid-area: to;
  `,
  subHeader: css`
    display: flex;
    align-items: center;
    margin-bottom: 4px;
    label {
      margin-right: 12px;
      font-size: 12px;
    }
  `,
  timezone: css`
    grid-area: timezone;
    label {
      font-size: 12px;
    }
  `,
  icons: css`
    grid-area: icons;
  `,

  formInputs: css`
    display: flex;
    flex-direction: row;
  `,
  timeInput: css`
    min-width: 220px;
    margin-right: 12px;
  `,
  quickList: css`
    display: flex;
    flex-direction: column;
    text-align: right;
  `,
  leftContainer: css`
    max-width: 450px;
  `,
  rightContainer: css`
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    min-width: 120px;
  `,
  timeZone: css`
    max-width: 450px;
    margin-top: 12px;
  `,
  shortcutBox: css`
    border: solid 1px ${theme.colors.border.weak};
    background-color: ${theme.colors.background.secondary};
    margin: 4px;
    padding: 2px;
    color: rgb(110, 159, 255);
    font-size: 10px;
  `,
  link: css`
    color: rgb(110, 159, 255);
    margin-bottom: 2px;
    font-size: 10px;
  `,
  copyPaste: css`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    gap: 8px;
    padding-top: 26px;
  `,
});
